/**
 * ReviewSessionPage — Milestone 37.3.
 *
 * Route entry for the Focus-Flow-powered guided review of a single
 * campaign pack. Composes:
 *
 *   MissionShell (37.1) + ReviewAssetPreview + ReviewDecisionBar
 *   + ReviewCompletionHandoff
 *
 * State machine (locked):
 *   loading      → queue fetching
 *   active       → showing MissionShell with asset[currentIndex]
 *   advancing    → 240 ms transition to the next asset
 *   completed    → showing MissionCompleteHandoff (never a dead-end)
 *   empty        → queue was empty on entry
 *
 * Doctrine (locked):
 *   * Every decision routes through the existing transitionAsset endpoint.
 *   * Never mutates campaign business logic.
 *   * Every pause persists memory pointers via useWriteReviewMemory.
 *   * Every advance auto-progresses to the next asset.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";

import { useAuthStore } from "@/entities/auth/store";
import { useCurrentWorkspace } from "@/entities/workspace";
import {
  useReviewQueue,
  useInvalidateReviewQueue,
} from "@/entities/review-queue";
import {
  useSyncCampaignMission,
  useWriteReviewMemory,
  useFindCampaignMission,
} from "@/entities/missions";
import { addComment, transitionAsset } from "@/entities/ops/api";
import { editAsset, regenerateRenderedImage, renderImageAsset } from "@/entities/ai-studio/api";
import type { Mission } from "@/entities/missions";
import { useUpgradePrompt } from "@/features/entitlements/useEntitlements";
import { MissionShell } from "@/features/focus-flow";
import type { MissionPrimaryActionState } from "@/features/focus-flow";

import { ReviewAssetPreview } from "./ReviewAssetPreview";
import { ReviewDecisionBarSecondary } from "./ReviewDecisionBar";
import { ReviewRegenerateDialog } from "./ReviewRegenerateDialog";
import { ReviewReturnDialog } from "./ReviewReturnDialog";
import {
  ReviewCompletionHandoff,
  type CompletionHandoffKind,
} from "./ReviewCompletionHandoff";

type PhaseState =
  | { phase: "loading" }
  | { phase: "empty" }
  | { phase: "active"; index: number }
  | { phase: "advancing"; index: number }
  /* Patch 37.4.22b — Truthful state for a `?asset=` param that is
   * not review-eligible in the current pack queue. Never silently
   * falls back to queue item 0 — the founder gets an explicit
   * choice. */
  | { phase: "requested_not_in_queue"; requestedAssetId: string; anomaly: null }
  /* Patch 37.4.22b — Ambiguity guard. If malformed queue data
   * surfaces multiple entries with the same `permanent_asset_id`,
   * we deterministically pick the row with the highest
   * `version_number` (canonical current head shape) AND surface
   * the anomaly for observability. Never ambiguous-target. */
  | { phase: "active"; index: number; anomaly: { permanent_asset_id: string; matches: number } }
  | {
      phase: "completed";
      kind: CompletionHandoffKind;
      decisionsMade: number;
    };

const TRANSITION_MS = 240;

const UNRESOLVED_PLACEHOLDER = /\[(?:insert|add|replace|your|company|business|address|hours|phone|email|website|url|link|date|time|location|disclaimer|name|details?|cta|qr)(?:[^\]\n]{0,80})\]/i;

export function hasUnresolvedAssetPlaceholder(body: string | null | undefined): boolean {
  return Boolean(body && UNRESOLVED_PLACEHOLDER.test(body));
}

function pickCompletionKind(mission: Mission | null): CompletionHandoffKind {
  const marker = (mission?.memory.data.lifecycle_marker as string) || "";
  if (marker === "vault") return "ready_to_schedule";
  if (marker === "schedule" || marker === "complete") return "already_scheduling";
  if (marker === "approve") return "vault_registration";
  return "back_to_campaign";
}

export function ReviewSessionPage(): JSX.Element {
  const { packId } = useParams<{ packId: string }>();
  const [searchParams] = useSearchParams();
  // Patch 37.4.22b · Read the initiating asset identity out of the
  // URL. The Vault drawer's "Review Final Media →" CTA encodes the
  // asset here so the founder lands on the exact head they clicked
  // — never queue item 0. Trimmed to reject whitespace-only params.
  const requestedAssetId = (searchParams.get("asset") ?? "").trim() || null;
  const workspace = useCurrentWorkspace();
  const workspaceId = workspace?.id ?? null;
  const userId = useAuthStore((s) => s.user?.id) ?? null;
  const navigate = useNavigate();

  const findCampaignMission = useFindCampaignMission(workspaceId);
  const invalidateReviewQueue = useInvalidateReviewQueue();
  const upgrade = useUpgradePrompt();

  const queueQuery = useReviewQueue(workspaceId, packId ?? null);
  const queue = queueQuery.data;
  const blueprintId = queue?.blueprint_id ?? null;

  const syncMutation = useSyncCampaignMission(workspaceId, userId);
  const writeMemory = useWriteReviewMemory(workspaceId, userId);

  // Session-scoped counters.
  const decisionsRef = useRef(0);
  // Captured at session start — the "of N" denominator stays stable
  // as the founder progresses so the mission feels like momentum, not
  // a shrinking list.
  const initialTotalRef = useRef<number | null>(null);
  // Frozen snapshot of the review queue items at session start. This
  // is what the session iterates through — even if the server-side
  // queue shrinks as we approve/return, the local pointer remains
  // meaningful. Drift (external mutations) is handled by rechecking
  // asset lifecycle at commit time, not by re-indexing.
  const snapshotRef = useRef<import("@/entities/review-queue").ReviewQueueItem[] | null>(null);
  const [phase, setPhase] = useState<PhaseState>({ phase: "loading" });
  const [returnDialogOpen, setReturnDialogOpen] = useState(false);
  const [regenerateDialogOpen, setRegenerateDialogOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draftBody, setDraftBody] = useState("");
  const [editedBodies, setEditedBodies] = useState<Record<string, string>>({});

  // ── Effect: initialize the session once the queue is loaded ──
  const initialized = useRef(false);
  useEffect(() => {
    if (initialized.current) return;
    if (queueQuery.isLoading) return;
    if (!queue) return;
    initialized.current = true;
    initialTotalRef.current = queue.total_pending;
    snapshotRef.current = [...queue.items];

    if (queue.total_pending === 0) {
      setPhase({ phase: "empty" });
      return;
    }

    // ── Patch 37.4.22b · Seed the cursor at the requested asset ──
    // The queue is already workspace + pack scoped, so a lookup by
    // identity here can never surface a cross-workspace or cross-pack
    // asset — the resolver just returns "not found" and we surface a
    // truthful state to the founder instead of silently trampling
    // onto queue item 0.
    let initialIndex = 0;
    let anomaly: { permanent_asset_id: string; matches: number } | null = null;
    if (requestedAssetId) {
      // Match by permanent_asset_id first (the customer-facing shape
      // encoded in the CTA), then by internal head asset_id (accepts
      // both so a bookmarked URL from either identifier resolves).
      const permanentMatches = queue.items
        .map((it, idx) => ({ it, idx }))
        .filter(({ it }) => it.permanent_asset_id === requestedAssetId);
      const headMatches = queue.items
        .map((it, idx) => ({ it, idx }))
        .filter(({ it }) => it.asset_id === requestedAssetId);
      const matches = permanentMatches.length > 0 ? permanentMatches : headMatches;

      if (matches.length === 0) {
        // Truthful state — never silently drop the founder onto a
        // different asset. They get an explicit "continue with pack
        // review" choice below.
        setPhase({ phase: "requested_not_in_queue", requestedAssetId, anomaly: null });
        return;
      }

      if (matches.length === 1) {
        initialIndex = matches[0].idx;
      } else {
        // Ambiguity guard — the pack-scoped review queue should only
        // ever return one head per permanent_asset_id (its filter is
        // is_head=True, superseded != true, status=needs_review), so
        // multiple matches means malformed data upstream. We pick a
        // deterministic canonical target (highest version_number,
        // then earliest queue position) AND surface the anomaly for
        // observability. Never allow ambiguous targeting.
        const canonical = matches.slice().sort((a, b) => {
          const va = (a.it as { version_number?: number }).version_number ?? -1;
          const vb = (b.it as { version_number?: number }).version_number ?? -1;
          if (va !== vb) return vb - va;
          return a.idx - b.idx;
        })[0];
        initialIndex = canonical.idx;
        anomaly = {
          permanent_asset_id: requestedAssetId,
          matches: matches.length,
        };
      }
    }

    setPhase(
      anomaly
        ? { phase: "active", index: initialIndex, anomaly }
        : { phase: "active", index: initialIndex },
    );

    // Best-effort: seed mission memory with the review-session
    // pointers so pausing/reloading lands here again.
    if (blueprintId && workspaceId && packId) {
      // Fire (idempotent) sync first so the campaign mission exists.
      syncMutation.mutate(blueprintId, {
        onSuccess: () => {
          writeMemory.mutate({
            blueprintId,
            pointers: {
              review_pack_id: packId,
              review_asset_id: queue.items[initialIndex]?.asset_id ?? null,
              review_position: initialIndex,
              review_total_at_pause: queue.total_pending,
              review_paused_at: null,
            },
          });
        },
      });
    }
  }, [queueQuery.isLoading, queue, blueprintId, packId, workspaceId, syncMutation, writeMemory, requestedAssetId]);

  // ── Decision mutation ────────────────────────────────────
  const decisionMutation = useMutation({
    mutationFn: async (input: {
      assetId: string;
      to: "approved" | "draft" | "archived";
      note?: string;
    }) => {
      if (!workspaceId) throw new Error("workspace required");
      // Best-effort — if the founder provided a revision note, attach
      // it before the transition so history reflects the intent.
      if (input.note && input.to === "draft") {
        try {
          await addComment(workspaceId, input.assetId, {
            type: "revision_note",
            body: input.note,
          });
        } catch {
          /* non-blocking */
        }
      }
      return transitionAsset(workspaceId, input.assetId, input.to);
    },
  });

  // ── Patch 37.4.21 · Regenerate rendered head ────────────
  // The mutation fires only after the founder acknowledges cost + fills
  // the optional feedback. Backend resolves the canonical source spec
  // via lineage — we never regenerate from the rendered head as if it
  // were the source prompt. On success the queue is invalidated so
  // the new v3 head lands in the founder's session on next resume.
  const regenerateMutation = useMutation({
    mutationFn: async (input: {
      renderedAssetId: string;
      feedback_note: string | null;
    }) => {
      if (!workspaceId) throw new Error("workspace required");
      return regenerateRenderedImage(workspaceId, input.renderedAssetId, {
        feedback_note: input.feedback_note ?? undefined,
        cost_ack: true,
      });
    },
  });

  const currentIndex = phase.phase === "active" || phase.phase === "advancing"
    ? phase.index
    : 0;
  const currentItem = queue?.items[currentIndex] ?? null;
  const currentBody = currentItem
    ? (editedBodies[currentItem.asset_id] ?? currentItem.full_text)
    : null;
  const currentDisplayItem = currentItem
    ? { ...currentItem, full_text: currentBody, preview_text: currentBody ?? currentItem.preview_text }
    : null;
  const currentIsRendered = currentItem?.has_media === true;
  const currentIsImageSpecification = currentItem != null && (
    currentItem.payload_maturity === "specification" ||
    currentItem.content_type.startsWith("image_prompt_")
  ) && !currentItem.has_media;
  const currentIsFullyInspectable = currentItem != null && (
    currentIsRendered
      ? Boolean(currentItem.media_preview_url)
      : Boolean(currentBody)
  );
  const currentHasUnresolvedPlaceholder = hasUnresolvedAssetPlaceholder(currentBody);
  // Total: the stable count captured at session start. Falls back to
  // the current queue length for the very first render.
  const total = initialTotalRef.current ?? queue?.total_pending ?? 0;

  useEffect(() => {
    setEditing(false);
    setDraftBody(currentBody ?? "");
  }, [currentItem?.asset_id, currentBody]);

  const editMutation = useMutation({
    mutationFn: async () => {
      if (!workspaceId || !currentItem) throw new Error("Asset unavailable");
      return editAsset(workspaceId, currentItem.asset_id, draftBody);
    },
    onSuccess: (updated) => {
      const saved = updated.body_markdown ?? draftBody;
      setEditedBodies((existing) => ({ ...existing, [updated.id]: saved }));
      setDraftBody(saved);
      setEditing(false);
      toast.success("Asset updated");
    },
    onError: (error: Error) => toast.error(error.message || "Edit failed"),
  });

  const renderImageMutation = useMutation({
    mutationFn: async () => {
      if (!workspaceId || !currentItem) throw new Error("Asset unavailable");
      return renderImageAsset(workspaceId, currentItem.asset_id);
    },
    onSuccess: async () => {
      if (workspaceId && packId) await invalidateReviewQueue(workspaceId, packId);
      toast.success("Image rendered. Opening the final image for review.");
      window.location.reload();
    },
    onError: (error: unknown) => {
      const err = error as { status?: number; code?: string; details?: Record<string, unknown>; message?: string };
      if (err.status === 402 || err.code === "premium_images_allowance_exhausted") {
        upgrade.open({
          title: "Monthly image capacity reached",
          reason: err.message || "Upgrade, add image capacity, or wait for the monthly reset.",
          upgrade_target: err.details?.upgrade_target as string | undefined,
          addon_kind: "images",
        });
        return;
      }
      toast.error(err.message || "Image render failed");
    },
  });

  function advanceOrComplete(nextIndex: number) {
    const items = snapshotRef.current ?? queue?.items ?? [];
    if (nextIndex >= items.length) {
      // Completion. Sync mission so lifecycle_marker + resume_route
      // reflect the new canonical state.
      const kindPromise = (async (): Promise<CompletionHandoffKind> => {
        if (!blueprintId || !workspaceId) return "back_to_campaign";
        try {
          const mission = await syncMutation.mutateAsync(blueprintId);
          return pickCompletionKind(mission);
        } catch {
          return "back_to_campaign";
        }
      })();
      kindPromise.then((kind) => {
        setPhase({
          phase: "completed",
          kind,
          decisionsMade: decisionsRef.current,
        });
        // Announce completion to screen readers.
        toast.success("Session complete", { duration: 2500 });
      });
      return;
    }
    // Move to the next asset via a short transition. The shell chrome
    // never remounts; only the body swaps.
    setPhase({ phase: "advancing", index: nextIndex });
    const reduced = typeof window !== "undefined"
      && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    window.setTimeout(() => {
      setPhase({ phase: "active", index: nextIndex });
      // Update mission memory with new position (best-effort).
      const items = snapshotRef.current ?? queue?.items ?? [];
      if (blueprintId && workspaceId && packId && items[nextIndex]) {
        writeMemory.mutate({
          blueprintId,
          pointers: {
            review_pack_id: packId,
            review_asset_id: items[nextIndex].asset_id,
            review_position: nextIndex,
            review_total_at_pause: total,
            review_paused_at: null,
          },
        });
      }
    }, reduced ? 0 : TRANSITION_MS);
  }

  async function commitDecision(
    to: "approved" | "draft" | "archived",
    note?: string,
  ) {
    if (!currentItem || !workspaceId) return;
    if (to === "approved" && (!currentIsFullyInspectable || editing || currentHasUnresolvedPlaceholder)) {
      toast.error(
        currentHasUnresolvedPlaceholder
          ? "Resolve every bracketed placeholder before approving this asset."
          : "Open the complete asset before approving it.",
      );
      return;
    }
    try {
      await decisionMutation.mutateAsync({
        assetId: currentItem.asset_id,
        to,
        note,
      });
      decisionsRef.current += 1;
      const label =
        to === "approved" ? "✓ Approved"
        : to === "draft" ? "✓ Returned for revision"
        : "✓ Archived";
      toast.success(`${label} — Loading next asset…`, { duration: 1800 });
      // Do NOT invalidate the review queue during the session — the
      // founder is iterating through the session-start snapshot, and
      // shrinking the queue mid-flight would race with our local
      // pointer. The queue is refreshed on session completion.
      advanceOrComplete(currentIndex + 1);
    } catch (err) {
      toast.error(
        err instanceof Error
          ? `Couldn't ${to === "approved" ? "approve" : to === "draft" ? "return" : "archive"} — please retry.`
          : "Decision failed.",
      );
    }
  }

  // ── Pause ────────────────────────────────────────────────
  async function pauseAndExit() {
    if (blueprintId && packId && currentItem) {
      writeMemory.mutate({
        blueprintId,
        pointers: {
          review_pack_id: packId,
          review_asset_id: currentItem.asset_id,
          review_position: currentIndex,
          review_total_at_pause: total,
          review_paused_at: new Date().toISOString(),
        },
      });
    }
    // Attempt to pause the mission itself. Best-effort — mission
    // pointers remain even if this call fails.
    if (blueprintId && workspaceId) {
      try {
        const mission = await findCampaignMission(blueprintId);
        if (mission) {
          const { pauseMission } = await import("@/entities/missions/api");
          await pauseMission(workspaceId, mission.id, mission.version);
        }
      } catch {
        /* non-blocking */
      }
    }
    navigate("/home");
  }

  // Keyboard shortcuts — desktop only.
  useEffect(() => {
    if (phase.phase !== "active") return;
    const onKey = (e: KeyboardEvent) => {
      if (
        e.target instanceof HTMLElement &&
        (e.target.tagName === "TEXTAREA" || e.target.tagName === "INPUT")
      ) return;
      if (e.key === "Enter") {
        e.preventDefault();
        if (!decisionMutation.isPending && currentIsFullyInspectable && !editing && !currentHasUnresolvedPlaceholder) void commitDecision("approved");
      } else if (e.key.toLowerCase() === "r") {
        e.preventDefault();
        if (!decisionMutation.isPending) setReturnDialogOpen(true);
      } else if (e.key === "Escape") {
        e.preventDefault();
        void pauseAndExit();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase.phase, currentItem?.asset_id, decisionMutation.isPending]);

  const primaryActionState: MissionPrimaryActionState = useMemo(() => {
    if (phase.phase === "advancing") return "pending";
    if (decisionMutation.isPending || renderImageMutation.isPending) return "pending";
    if (editMutation.isPending) return "pending";
    if (!currentIsFullyInspectable || editing || currentHasUnresolvedPlaceholder) return "disabled";
    return "idle";
  }, [phase.phase, decisionMutation.isPending, renderImageMutation.isPending, editMutation.isPending, currentIsFullyInspectable, currentHasUnresolvedPlaceholder, editing]);

  // Compute the mission shim used by the shell. Kept as a memo so its
  // identity is stable across renders and hooks-order rules are
  // preserved regardless of the render branch below.
  const missionShim: Mission = useMemo(() => ({
    id: `review-session:${packId}`,
    workspace_id: workspaceId ?? "",
    mission_type: "campaign",
    subject_id: blueprintId ?? null,
    created_by: userId ?? "",
    status: "active",
    stage: "execution",
    progress: {
      current: currentIndex + 1,
      total,
      step_key: "review",
    },
    memory: {
      schema_version: 1,
      definition_version: 1,
      data: {
        blueprint_id: blueprintId,
        pack_id: packId,
        review_pack_id: packId,
      },
    },
    version: 0,
    priority: 50,
    created_at: "",
    updated_at: "",
    paused_at: null,
    resumed_at: null,
    completed_at: null,
    cancelled_at: null,
  } as unknown as Mission), [packId, workspaceId, blueprintId, userId, currentIndex, total]);

  // ── Render ───────────────────────────────────────────────

  // Missing workspace / pack id — bail cleanly.
  if (!packId) {
    return (
      <div className="p-8 text-white/70" data-testid="review-session-invalid">
        Missing pack id. <button onClick={() => navigate("/home")} className="underline">Return to Executive Home</button>.
      </div>
    );
  }
  if (queueQuery.isError) {
    return (
      <div className="p-8 text-white/70" data-testid="review-session-error">
        <p>We couldn't load the review queue. Please try again.</p>
        <button
          onClick={() => queueQuery.refetch()}
          className="mt-4 rounded-full border border-white/15 px-4 py-2 text-white/85 hover:bg-white/5"
          data-testid="review-session-retry"
        >
          Retry
        </button>
      </div>
    );
  }

  if (phase.phase === "loading" || !queue) {
    return (
      <div
        className="p-8 text-white/60"
        data-testid="review-session-loading"
        aria-live="polite"
      >
        Preparing your review session…
      </div>
    );
  }

  if (phase.phase === "empty") {
    return (
      <div data-testid="review-session-root" className="min-h-[60vh] py-8">
        <ReviewCompletionHandoff
          kind="no_work_needed"
          campaignName={queue.campaign_name}
          decisionsMade={0}
          blueprintId={blueprintId}
          packId={packId}
        />
      </div>
    );
  }

  if (phase.phase === "completed") {
    return (
      <div data-testid="review-session-root" className="min-h-[60vh] py-8">
        <ReviewCompletionHandoff
          kind={phase.kind}
          campaignName={queue.campaign_name}
          decisionsMade={phase.decisionsMade}
          blueprintId={blueprintId}
          packId={packId}
          onPrimaryOverride={
            phase.kind === "more_items_pending"
              ? () => {
                  // Reset session in-place.
                  decisionsRef.current = 0;
                  initialized.current = false;
                  setPhase({ phase: "loading" });
                  void queueQuery.refetch();
                }
              : undefined
          }
        />
      </div>
    );
  }

  // Patch 37.4.22b — Truthful state for a `?asset=` param that is
  // not review-eligible in the current pack queue. Never silently
  // falls back to queue item 0.
  if (phase.phase === "requested_not_in_queue") {
    const total = queue.total_pending;
    return (
      <div
        data-testid="review-session-requested-not-in-queue"
        data-requested-asset-id={phase.requestedAssetId}
        className="min-h-[60vh] py-8"
      >
        <div className="mx-auto max-w-2xl rounded-2xl border border-amber-400/25 bg-amber-400/5 p-6">
          <h2 className="text-lg font-semibold text-amber-100">
            Asset not currently in review
          </h2>
          <p className="mt-2 text-sm text-amber-100/85">
            <span className="font-mono text-amber-100">{phase.requestedAssetId}</span>{" "}
            isn't waiting in this campaign's review queue. It may have already
            been approved, archived, or superseded by a newer version.
          </p>
          <p className="mt-1 text-xs text-amber-100/60">
            {total > 0
              ? `${total} other item${total === 1 ? "" : "s"} still pending in this campaign.`
              : "No other items pending in this campaign either."}
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            {total > 0 ? (
              <button
                type="button"
                data-testid="review-session-continue-pack-review"
                className="rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
                onClick={() => setPhase({ phase: "active", index: 0 })}
              >
                Continue with pack review →
              </button>
            ) : null}
            <button
              type="button"
              data-testid="review-session-back-to-vault"
              className="rounded-full border border-white/15 px-4 py-2 text-sm font-medium text-white/85 hover:bg-white/5"
              onClick={() => navigate("/library")}
            >
              Back to Vault
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Active or advancing.
  const advancing = phase.phase === "advancing";
  // Patch 37.4.22b · Ambiguity anomaly (multiple queue rows matched
  // the requested `permanent_asset_id`). We already picked the
  // canonical highest-version_number row above — this surfaces the
  // anomaly for the founder + captures a testid for observability.
  const anomaly =
    phase.phase === "active" && "anomaly" in phase ? phase.anomaly : null;

  return (
    <div
      data-testid="review-session-root"
      data-snapshot-len={snapshotRef.current?.length ?? -1}
      data-current-index={currentIndex}
      className="min-h-[60vh]"
    >
      <MissionShell
        mission={missionShim}
        missionTitle={queue.campaign_name || "Reviewing campaign"}
        stageLabel={`Reviewing · Asset ${currentIndex + 1} of ${total}`}
        primaryActionLabel={currentIsImageSpecification ? "Render image →" : "Approve →"}
        primaryActionState={primaryActionState}
        onPrimaryAction={() => {
          if (currentIsImageSpecification) {
            renderImageMutation.mutate();
          } else if (currentIsFullyInspectable && !editing && !currentHasUnresolvedPlaceholder) {
            void commitDecision("approved");
          }
        }}
        onPause={() => void pauseAndExit()}
        status={
          advancing
            ? "Loading next asset…"
            : currentIsImageSpecification
              ? "Render the image, or return the specification for revision"
              : "Approve, or return for revision"
        }
        variant="auto"
        contentSize="wide"
        testIdRoot="review-mission-shell"
      >
        {currentItem ? (
          <div
            className={
              "transition-opacity duration-200 " +
              (advancing ? "opacity-30" : "opacity-100")
            }
            data-testid="review-session-body"
            data-advancing={advancing ? "true" : "false"}
          >
            {anomaly ? (
              <div
                data-testid="review-session-ambiguity-banner"
                data-ambiguity-permanent-asset-id={anomaly.permanent_asset_id}
                data-ambiguity-matches={anomaly.matches}
                className="mb-4 rounded-lg border border-amber-400/25 bg-amber-400/10 px-3 py-2 text-xs text-amber-100/85"
                role="status"
              >
                Data anomaly: {anomaly.matches} queue entries claim the same
                canonical identity{" "}
                <span className="font-mono">{anomaly.permanent_asset_id}</span>.
                Reviewing the highest-version_number row.
              </div>
            ) : null}
            <ReviewAssetPreview
              item={currentDisplayItem ?? currentItem}
              index={currentIndex}
              total={total}
            />
            {!currentIsRendered && currentBody ? (
              <div className="mt-4" data-testid="review-session-edit-surface">
                {editing ? (
                  <div className="space-y-3">
                    <textarea
                      data-testid="review-session-edit-body"
                      className="min-h-64 w-full rounded-xl border border-white/15 bg-black/30 p-4 text-sm text-white"
                      value={draftBody}
                      onChange={(event) => setDraftBody(event.target.value)}
                    />
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        className="rounded-full border border-white/15 px-4 py-2 text-sm text-white/80"
                        onClick={() => { setEditing(false); setDraftBody(currentBody); }}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        data-testid="review-session-save-edit"
                        className="rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
                        disabled={editMutation.isPending || !draftBody.trim()}
                        onClick={() => editMutation.mutate()}
                      >
                        {editMutation.isPending ? "Saving…" : "Save changes"}
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    data-testid="review-session-edit"
                    className="rounded-full border border-white/15 px-4 py-2 text-sm font-medium text-white/85 hover:bg-white/5"
                    onClick={() => setEditing(true)}
                  >
                    Edit
                  </button>
                )}
              </div>
            ) : null}
            {!currentIsFullyInspectable ? (
              <p className="mt-4 text-sm text-amber-200" data-testid="review-session-inspection-blocked">
                Full asset content is unavailable. Approval is disabled until it can be completely inspected.
              </p>
            ) : null}
            {currentHasUnresolvedPlaceholder ? (
              <p className="mt-4 text-sm text-amber-200" data-testid="review-session-placeholder-blocked">
                This asset still contains bracketed placeholders. Edit and replace them before approval.
              </p>
            ) : null}
            <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="review-session-navigation">
              <button
                type="button"
                data-testid="review-session-previous"
                className="rounded-full border border-white/15 px-4 py-2 text-sm font-medium text-white/80 hover:bg-white/5 disabled:opacity-40"
                disabled={currentIndex === 0 || decisionMutation.isPending || advancing || editing}
                onClick={() => advanceOrComplete(currentIndex - 1)}
              >
                Previous
              </button>
              <button
                type="button"
                data-testid="review-session-next"
                className="rounded-full border border-white/15 px-4 py-2 text-sm font-medium text-white/80 hover:bg-white/5 disabled:opacity-40"
                disabled={currentIndex >= (snapshotRef.current?.length ?? queue.items.length) - 1 || decisionMutation.isPending || advancing || editing}
                onClick={() => advanceOrComplete(currentIndex + 1)}
              >
                Next
              </button>
              <button
                type="button"
                data-testid="review-session-skip"
                className="rounded-full border border-white/15 px-4 py-2 text-sm font-medium text-white/80 hover:bg-white/5 disabled:opacity-50"
                disabled={decisionMutation.isPending || advancing || regenerateMutation.isPending || editMutation.isPending || editing}
                onClick={() => advanceOrComplete(currentIndex + 1)}
              >
                Skip for now
              </button>
              <div className="ml-auto">
                <ReviewDecisionBarSecondary
                  onReturn={() => setReturnDialogOpen(true)}
                  onArchive={() => void commitDecision("archived")}
                  onRegenerate={
                    currentItem.payload_maturity === "rendered_deliverable"
                      ? () => setRegenerateDialogOpen(true)
                      : undefined
                  }
                  disabled={decisionMutation.isPending || advancing || regenerateMutation.isPending}
                />
              </div>
            </div>
          </div>
        ) : (
          // Fallback — surfaces the position label even when the
          // currentItem is momentarily unresolvable (e.g. between an
          // invalidation and the next render). Keeps the position
          // testid stable so E2E specs don't race the transition.
          <div className="text-white/40 text-sm" data-testid="review-session-body">
            <span data-testid="review-asset-position">
              Asset {currentIndex + 1} of {total}
            </span>
          </div>
        )}
      </MissionShell>

      <ReviewReturnDialog
        open={returnDialogOpen}
        onCancel={() => setReturnDialogOpen(false)}
        onConfirm={(note) => {
          setReturnDialogOpen(false);
          void commitDecision("draft", note || undefined);
        }}
        isPending={decisionMutation.isPending}
      />

      {/* Patch 37.4.21 — In-context controlled regeneration for
          rendered deliverables. Never fires paid render on click. */}
      <ReviewRegenerateDialog
        open={regenerateDialogOpen}
        isPending={regenerateMutation.isPending}
        permanentAssetId={currentItem?.permanent_asset_id ?? null}
        onCancel={() => setRegenerateDialogOpen(false)}
        onOpenAiStudio={() => {
          setRegenerateDialogOpen(false);
          if (currentItem?.asset_id) {
            navigate(`/ai-studio?asset=${currentItem.asset_id}`);
          } else {
            navigate("/ai-studio");
          }
        }}
        onConfirm={(payload) => {
          if (!currentItem) return;
          regenerateMutation.mutate(
            {
              renderedAssetId: currentItem.asset_id,
              feedback_note: payload.feedback_note,
            },
            {
              onSuccess: () => {
                toast.success(
                  "New version submitted. It will appear here once ready.",
                );
                setRegenerateDialogOpen(false);
                // Refresh the queue so any newly-created head shows up.
                void queueQuery.refetch();
              },
              onError: (err: unknown) => {
                const capacityError = err as {
                  status?: number;
                  code?: string;
                  details?: Record<string, unknown>;
                  message?: string;
                };
                if (
                  capacityError.status === 402 ||
                  capacityError.code === "premium_images_allowance_exhausted"
                ) {
                  upgrade.open({
                    title: "Monthly image capacity reached",
                    reason:
                      capacityError.message ||
                      "Upgrade, add image capacity, or wait for the monthly reset.",
                    upgrade_target: capacityError.details?.upgrade_target as
                      | string
                      | undefined,
                    addon_kind: "images",
                  });
                  return;
                }
                const message =
                  err instanceof Error
                    ? err.message
                    : "Could not start regeneration — please retry.";
                toast.error(message);
              },
            },
          );
        }}
      />
    </div>
  );
}
